import { User, UserFormData } from "../types"
import { ID, PaginationResult } from "@/types/common"
import type { components } from "@/types/api"
import { createCrudApi } from "@/utils/apiFactory"
import { UserStatus } from "../types"

const baseApi = createCrudApi<User, components["schemas"]["CreateUserDto"], components["schemas"]["UpdateUserDto"]>(
  "/users"
)

type CreateUserDto = components["schemas"]["CreateUserDto"]
type UpdateUserDto = components["schemas"]["UpdateUserDto"]

type RawUser = User & {
  roles?: Array<{
    role?: {
      id?: ID
      name?: string
      code?: string
    }
  }>
}

function normalizeUser(raw: RawUser): User {
  const roles = raw.roles ?? []

  const roleIds = roles
    .map((item) => item.role?.id)
    .filter((id): id is ID => id !== undefined && id !== null)
  const roleNames = roles
    .map((item) => item.role?.name)
    .filter((name): name is string => Boolean(name))

  return {
    ...raw,
    roleIds,
    roleNames,
  }
}

function toNumberIds(ids?: ID[]): number[] | undefined {
  if (!ids || ids.length === 0) return undefined
  return ids
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
}

// 用户API
const userApi = {
  // 查询所有（支持分页参数，但当前实现不使用）
  async getList(
    params?: {
      page?: number
      pageSize?: number
      roleCode?: string
      status?: UserStatus
    }
  ): Promise<PaginationResult<User>> {
    const result = await baseApi.list(
      params
        ? {
            page: params.page?.toString(),
            pageSize: params.pageSize?.toString(),
            roleCode: params.roleCode,
            status: params.status,
          }
        : undefined
    )

    return {
      ...result,
      data: (result.data ?? []).map((item) => normalizeUser(item as RawUser)),
    }
  },

  // ID查询
  async getById(id: ID): Promise<User | null> {
    const result = await baseApi.getById(id)
    if (!result) return null
    return normalizeUser(result as RawUser)
  },

  // 创建
  async create(data: UserFormData): Promise<void> {
    const roleIds = toNumberIds(data.roleIds) ?? []
    const payload: CreateUserDto = {
      code: data.code,
      name: data.name,
      avatar: data.avatar,
      roleIds,
    }
    await baseApi.create(payload)
  },

  // 更新
  async update(id: ID, data: UserFormData): Promise<void> {
    const payload: UpdateUserDto = {
      code: data.code,
      name: data.name,
      avatar: data.avatar,
      status: data.status,
      roleIds: toNumberIds(data.roleIds),
    }
    await baseApi.update(id, payload)
  },

  // 删除（统一使用数组参数，支持单个和批量）
  async delete(ids: ID[]): Promise<void> {
    await baseApi.delete(ids)
  },
}

export default userApi
